"use client"

import type React from "react"

import { useState } from "react"
import { useToast } from "@/hooks/use-toast"

const fieldBase =
  "flex h-11 w-full border border-[color-mix(in_oklch,var(--home-navy)_22%,transparent)] bg-[color-mix(in_oklch,var(--home-paper-2)_35%,transparent)] px-4 text-sm text-[var(--home-ink)] outline-none transition-colors duration-200 placeholder:text-[color-mix(in_oklch,var(--home-ink)_42%,transparent)] focus:border-[var(--home-gold)]"

export function ContactForm() {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        toast({
          title: "Message Sent!",
          description: "Thank you for reaching out. We'll get back to you.",
        })
        setFormData({
          name: "",
          email: "",
          company: "",
          subject: "",
          message: "",
        })
      } else {
        throw new Error("Failed to send message")
      }
    } catch (error) {
      toast({
        title: "Failed to Send",
        description: "Please try again or contact us directly.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-[var(--home-navy)]">
            Full Name *
          </label>
          <input id="name" className={fieldBase} value={formData.name} onChange={(e) => handleChange("name", e.target.value)} required />
        </div>
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-[var(--home-navy)]">
            Email Address *
          </label>
          <input
            id="email"
            type="email"
            className={fieldBase}
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="company" className="block text-sm font-medium text-[var(--home-navy)]">
          Company
        </label>
        <input id="company" className={fieldBase} value={formData.company} onChange={(e) => handleChange("company", e.target.value)} />
      </div>

      <div className="space-y-2">
        <label htmlFor="subject" className="block text-sm font-medium text-[var(--home-navy)]">
          Subject *
        </label>
        <div className="relative">
          <select
            id="subject"
            className={`${fieldBase} appearance-none pr-10`}
            value={formData.subject}
            onChange={(e) => handleChange("subject", e.target.value)}
            required
          >
            <option value="" disabled>
              Select a subject
            </option>
            <option value="event-inquiry">Event Inquiry</option>
            <option value="partnership">Partnership Opportunity</option>
            <option value="custom-program">Custom Program</option>
            <option value="media-press">Media & Press</option>
            <option value="general">General Question</option>
          </select>
          <i
            className="fa-solid fa-chevron-down pointer-events-none absolute right-4 top-0 my-auto h-3 w-3 text-[color-mix(in_oklch,var(--home-ink)_40%,transparent)]"
            aria-hidden
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="message" className="block text-sm font-medium text-[var(--home-navy)]">
          Message *
        </label>
        <textarea
          id="message"
          rows={5}
          className={`${fieldBase} min-h-32 resize-y py-3`}
          value={formData.message}
          onChange={(e) => handleChange("message", e.target.value)}
          placeholder="Tell us about your needs and how we can help..."
          required
        />
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="home-btn home-btn-gold w-full disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isLoading ? "Sending..." : "Send Message"}
        <i className="fa-regular fa-paper-plane fa-sm" aria-hidden />
      </button>
    </form>
  )
}